"use client";

import { QuantitySelector } from "@repo/components";
import { MAX_CART_ITEM_QTY } from "@repo/constants/cart";
import { CartContext } from "@repo/contexts/CartContext";
import { useFeatureFlag, useSnackbarHandler } from "@repo/lib/hooks/common";
import { useAddToCartEvent } from "@repo/lib/hooks/gtm";
import { useTrackAddToCart } from "@repo/lib/hooks/webengage";
import { addItemToCart } from "@repo/server-actions/cart";
import { Button, LoadingDots } from "@repo/ui";
import cx from "classnames";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useCallback, useContext, useState, useTransition } from "react";
const AddToCartButton = ({
  product,
  isInStock,
  isQtySelectorEnabled,
  variant,
  categorySlug,
  spaceSlug
}) => {
  const locale = useLocale();
  const [qty, setQty] = useState(1);
  const t = useTranslations("Common");
  const [isPending, startTransition] = useTransition();
  const {
    setShowMiniCart,
    cartTotalsInfo
  } = useContext(CartContext);
  const {
    handleAddToCartEvent
  } = useAddToCartEvent();
  const pathname = usePathname();
  const isProductInInfluencer = pathname?.includes("spaces");
  const isSpacesCollectionEnabled = useFeatureFlag("influencers");
  const {
    showSnackbar
  } = useSnackbarHandler();
  const {
    trackAddToCartEvent
  } = useTrackAddToCart();
  const handleAddToCart = useCallback(() => {
    // Find the existing item in the cart to check its quantity
    const existingItem = cartTotalsInfo?.lineItems?.find(item => item.id === product?.id);
    // If the product is not in the cart, we can add it directly or else we need to check the quantity
    const totalQty = (existingItem?.quantity || 0) + qty;
    // If the total quantity exceeds the maximum allowed, show an error message
    if (totalQty > MAX_CART_ITEM_QTY) {
      showSnackbar({
        type: "error",
        message: t("Maximum_Quantity_Is_Exceeded")
      });
      // do not proceed with adding to cart and return early
      return;
    }
    const payLoad = {
      id: product?.id,
      quantity: qty,
      ...(isSpacesCollectionEnabled && {
        // remove this condition once we push the influencer to prod
        type: isProductInInfluencer ? "influencer" : "product_category",
        slug: spaceSlug ? spaceSlug : categorySlug || product?.slug
      })
    };
    startTransition(async () => {
      const response = await addItemToCart(payLoad, locale);
      if (response?.success) {
        setQty(1);
        setShowMiniCart(true);
        handleAddToCartEvent(product, payLoad, response?.couponName);
        trackAddToCartEvent({
          ...product,
          ...payLoad,
          couponName: response?.couponName
        });
      }
      showSnackbar(response);
    });
  }, [startTransition, locale, setQty, setShowMiniCart, handleAddToCartEvent, product, showSnackbar, categorySlug, isProductInInfluencer, isSpacesCollectionEnabled, qty, spaceSlug, trackAddToCartEvent, cartTotalsInfo, t]);
  return <>
      {isQtySelectorEnabled && isInStock && <div className="border-miniCart-cartItems-quantityButtonBorder flex items-center border-y">
          <QuantitySelector qty={qty} setQty={setQty} type="minus" />
          <span className="text-miniCart-cartItems-quantityText min-w-[42px] max-w-[42px] px-3 text-center text-sm">
            {qty}
          </span>
          <QuantitySelector qty={qty} setQty={setQty} type="plus" />
        </div>}
      <Button onClick={handleAddToCart} variant={variant || "primary"} size="md" disabled={isPending || !isInStock} className={cx("relative pe-3 text-sm font-semibold md:text-base rtl:font-medium", {
      "ps-1.5": isPending || !isInStock,
      "ps-3": !isPending,
      "!bg-supportiveGray": isPending,
      "min-w-[102px] md:min-w-[153px] rtl:min-w-[128px] rtl:md:min-w-[183px]": isPending && variant === "primary",
      "min-h-fit min-w-[145px] whitespace-nowrap !px-3 md:portrait:min-w-0 md:portrait:max-w-[90px] md:portrait:text-sm rtl:md:portrait:text-[0.678rem]": variant === "secondary",
      "h-[38px] md:h-[46px]": variant === "primary" // primary button height for product details page to match with quantity selector
    })} data-sentry-element="Button" data-sentry-source-file="AddToCartButton.jsx">
        <span className="flex justify-center">
          {isPending ? <LoadingDots className="bg-button-primary-text" /> : <>{isInStock ? `+ ${t("Add_To_Cart")}` : `${t("Out_Of_Stock")}`}</>}
        </span>
      </Button>
    </>;
};
export default AddToCartButton;